import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { importHoursFromTripletex } from "../../../api/accounting";
import { toast } from "react-hot-toast";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export const AccountingTripletexImportPage = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await importHoursFromTripletex({
        from_date: fromDate,
        to_date: toDate,
        async: false,
      });
      if (response.error) {
        toast.error(response.data?.errors?.[0] || response.data?.error || t("accounting.import.error"));
      } else {
        setResult(response.data?.result);
        toast.success(t("accounting.import.success"));
      }
    } catch (err) {
      toast.error(t("accounting.import.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader title={t("accounting.menu.tripletex_import")} />

      <div className="mx-auto max-w-7xl w-full px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <p className="mb-4 text-sm text-gray-600">{t("accounting.import.description")}</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600">{t("accounting.import.from_date")}</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600">{t("accounting.import.to_date")}</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={handleImport}
          disabled={loading}
          className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? t("accounting.import.running") : t("accounting.import.run")}
        </button>

        {result && (
          <div className="mt-6 rounded-xl bg-gray-50 p-4 text-sm">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div>
                <div className="text-gray-500">{t("accounting.import.found")}</div>
                <div className="text-lg font-semibold">{result.found}</div>
              </div>
              <div>
                <div className="text-gray-500">{t("accounting.import.created")}</div>
                <div className="text-lg font-semibold text-green-600">{result.created}</div>
              </div>
              <div>
                <div className="text-gray-500">{t("accounting.import.updated")}</div>
                <div className="text-lg font-semibold text-blue-600">{result.updated}</div>
              </div>
              <div>
                <div className="text-gray-500">{t("accounting.import.skipped")}</div>
                <div className="text-lg font-semibold text-amber-600">{result.skipped_count}</div>
              </div>
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingTripletexImportPage;
